import { useQuery } from "@tanstack/react-query";
import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import Scrollbars from "react-custom-scrollbars-2";
import { partition } from "lodash";
import {
    CounterpartiesReturnFormat,
    DirectorySort,
} from "../../../../common/constants/counterparty";
import { RegisteredCounterpartyIncludeScope } from "../../../../common/helpers/counterparties";
import {
    counterpartiesQueryKeys,
    getCounterparties,
} from "../../../../api/counterparty.api";
import { useWorkspaceContext } from "../../../../state/workspaceContext";
import { FilterSearch } from "../../FilterSearch/FilterSearch";
import { Loader } from "../../Loader";
import { FilterActionsFooter } from "../../Filter/FilterActionsFooter/FilterActionsFooter";
import styles from "./CounterpartyFilter.module.scss";
import {
    CounterpartiesFilters,
    CounterpartiesWithEmptyValue,
    NO_COUNTERPARTY_LABEL,
} from "./lib";
import { CounterpartyFilterRow } from "./CounterpartyFilterRow";

interface Props {
    filters: CounterpartiesFilters;
    onChange: (value: CounterpartiesFilters) => void;
    hideNoVendor?: boolean;
}

export const CounterpartyFilter: React.FC<Props> = ({
    filters,
    onChange,
    hideNoVendor,
}) => {
    const { activeWorkspace } = useWorkspaceContext();
    const counterparties = useQuery({
        queryKey: counterpartiesQueryKeys.query(activeWorkspace?.id ?? ""),
        queryFn: () => {
            if (!activeWorkspace) {
                return { data: [] };
            }

            return getCounterparties(
                {
                    workspaceId: activeWorkspace.id,
                    sort: DirectorySort.NAME_ASC,
                    includeRegistered: RegisteredCounterpartyIncludeScope.USED,
                },
                CounterpartiesReturnFormat.JSON,
            );
        },
    });

    const [search, setSearch] = useState("");
    const searchRef = useRef<HTMLInputElement>();
    const selectedAtOpen = useRef<Array<string | null> | undefined>(
        filters.withEmptyOrUnverifiedCounterparty
            ? [null]
            : filters.counterpartiesIds,
    );

    useEffect(() => {
        searchRef.current?.focus();

        return () => {
            setSearch("");
        };
    }, []);

    const handleChange = useCallback(
        (counterpartyId: string) => {
            if (counterpartyId === "") {
                if (filters.withEmptyOrUnverifiedCounterparty) {
                    onChange({
                        counterpartiesIds: undefined,
                        withEmptyOrUnverifiedCounterparty: undefined,
                    });
                } else {
                    onChange({
                        counterpartiesIds: undefined,
                        withEmptyOrUnverifiedCounterparty: true,
                    });
                }
            } else if (filters.counterpartiesIds?.includes(counterpartyId)) {
                onChange({
                    counterpartiesIds: filters.counterpartiesIds.filter(
                        (id) => id !== counterpartyId,
                    ),
                    withEmptyOrUnverifiedCounterparty: undefined,
                });
            } else {
                onChange({
                    counterpartiesIds: [
                        ...(filters.counterpartiesIds ?? []),
                        counterpartyId,
                    ],
                    withEmptyOrUnverifiedCounterparty: undefined,
                });
            }
        },
        [
            filters.counterpartiesIds,
            filters.withEmptyOrUnverifiedCounterparty,
            onChange,
        ],
    );

    const displayedCounterparties = useMemo(() => {
        if (!counterparties.data) {
            return undefined;
        }

        const items: CounterpartiesWithEmptyValue = counterparties.data.data;
        const hasNoCounterpartyItem = items.some((i) => !i.counterparty);

        if (!hasNoCounterpartyItem) {
            items.push({
                counterparty: null,
            });
        }

        if (!search) {
            return items;
        }

        return items.filter(
            (item) =>
                item.counterparty?.name
                    .toLowerCase()
                    .includes(search.toLowerCase()) ??
                (item.counterparty === null &&
                    NO_COUNTERPARTY_LABEL.toLowerCase().includes(
                        search.toLowerCase(),
                    )),
        );
    }, [counterparties.data, search]);

    const [pinnedItems, otherItems] = useMemo(
        () =>
            partition(displayedCounterparties ?? [], (row) =>
                selectedAtOpen.current?.includes(row.counterparty?.id ?? null),
            ),
        [displayedCounterparties],
    );
    const [otherItemsWithoutNoVendor, noVendor] = useMemo(
        () => partition(otherItems, (item) => item.counterparty),
        [otherItems],
    );

    if (counterparties.data && !counterparties.data.data.length) {
        return (
            <p className="text-center mt-5 text-prominent">
                No Vendors/Customers created yet
            </p>
        );
    }

    return (
        <div className={styles.filter}>
            <FilterSearch
                value={search}
                onChange={setSearch}
                inputRef={searchRef}
            />
            <div className={styles.content}>
                {counterparties.isLoading && <Loader />}
                {displayedCounterparties && (
                    <Scrollbars style={{ width: "100%", height: "100%" }}>
                        {displayedCounterparties.length === 0 && (
                            <p className={styles.empty}>
                                No matching Vendor/Customer
                            </p>
                        )}
                        {pinnedItems.length > 0 && (
                            <div className={styles.section}>
                                {pinnedItems.map((item) => (
                                    <CounterpartyFilterRow
                                        key={item.counterparty?.id}
                                        counterparty={item.counterparty}
                                        filters={filters}
                                        handleChange={handleChange}
                                    />
                                ))}
                            </div>
                        )}
                        {otherItemsWithoutNoVendor.length > 0 && (
                            <div className={styles.section}>
                                {otherItemsWithoutNoVendor.map((item) => (
                                    <CounterpartyFilterRow
                                        key={item.counterparty?.id}
                                        counterparty={item.counterparty}
                                        filters={filters}
                                        handleChange={handleChange}
                                    />
                                ))}
                            </div>
                        )}
                        {noVendor.length > 0 && !hideNoVendor && (
                            <div className={styles.section}>
                                <CounterpartyFilterRow
                                    counterparty={null}
                                    filters={filters}
                                    handleChange={handleChange}
                                />
                            </div>
                        )}
                    </Scrollbars>
                )}
            </div>

            {!!filters.counterpartiesIds?.length && (
                <FilterActionsFooter
                    deselectFunction={() =>
                        onChange({ counterpartiesIds: undefined })
                    }
                    selectedCount={filters.counterpartiesIds.length}
                />
            )}
        </div>
    );
};
